import { getCookie, removeCookie } from '@shared/lib';

import { getLocale } from '@/i18n/locale';

import { CONSENT_REQUIRED_EVENT, HOUSE_RULE_REQUIRED_EVENT, isConsentRequiredError, isHouseRuleRequiredError } from './consent-required';
import { addRefreshSubscriber, onRefreshed, onRefreshFailed } from './helpers';
import { apiWithAuth } from './instances';
import { clearLegacyRefreshToken } from './legacy-refresh-cookie';
import { reissueSession } from './reissue-session';

import type { RefreshSubscriber } from './helpers';
import type { CustomAxiosRequestConfig } from '@shared/types';
import type { InternalAxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';

// 토큰 갱신 요청 상태
let isRefreshing = false;
const refreshSubscribers: RefreshSubscriber[] = [];

/**
 * TRAD-3662 — 화면 로케일을 `Accept-Language` 로 보낸다(두 인스턴스 공통).
 *
 * 종전엔 web 코드 어디에서도 이 헤더를 안 넣어 **브라우저 기본 언어**가 갔다. 실측(dev 2026-10-16 07:4x,
 * 화면 EN + ko-KR 브라우저): 모든 /api 요청이 `ko-KR,ko;q=0.9,en-US;q=0.8,en;q=0.7` 로 나가고
 * `/api/feeds/999999999` → 404 FEED_001 «피드정보가 없습니다.» — 클라 매핑 없는 code 의 서버 message 폴백이
 * EN 화면에 한국어로 떴다. 서버는 이 헤더로 ErrorCode message 를 고른다(backend ErrorCodeUtils, TRAD-3631 운영 #252).
 *
 * `@/i18n/locale` 은 카탈로그 json 을 끌어오지 않는 모듈이다 — shared/api 배럴이 SharedWorker 로 끌려가도
 * 번역 json 이 딸려가지 않는다(`error-localize.ts` 머리말의 제약). prerender(빌드 타임 Node fetch) 는
 * 이 인터셉터를 거치지 않으므로(prerender.mjs:98) 산출물 로케일(ko-KR)에 영향 없다.
 */
export const withLocaleRequestHandler = (request: InternalAxiosRequestConfig) => {
  request.headers['Accept-Language'] = getLocale();
  return request;
};

export const withAuthRequestHandler = (request: InternalAxiosRequestConfig) => {
  const accessToken = getCookie('accessToken');

  // 토큰이 존재하면 Authorization 헤더에 Bearer 토큰 추가
  if (accessToken) {
    request.headers.Authorization = `Bearer ${accessToken}`;
  }

  return request;
};

/** 세션 끝 — 쿠키 제거 + AuthContext 에 알림(종전 catch 본문 그대로). TRAD-3683 부터는 401·404 일 때만 부른다. */
const expireSession = () => {
  removeCookie('accessToken');
  // TRAD-3861 — 리프레시는 서버 HttpOnly 쿠키라 JS 가 못 지운다(서버가 401/404 를 낸 키는 이미 무효). 옛 JS 쿠키만 정리.
  clearLegacyRefreshToken();
  removeCookie('userId');
  removeCookie('email');
  window.dispatchEvent(new CustomEvent('auth:token-expired'));
};

export const responseSuccessHandler = async (response: AxiosResponse) => response;
export const responseErrorHandler = async (error: AxiosError) => {
  const status = error.response?.status;

  // G7 — 필수 미동의(403 TERMS_007): 동의 화면으로 보내라고 알린다. 에러 자체는 그대로 호출부로 간다(문구는 code 기반 S30).
  if (isConsentRequiredError(error)) window.dispatchEvent(new CustomEvent(CONSENT_REQUIRED_EVENT));
  // TRAD-3777 — 하우스 룰 미동의(403 TERMS_008): 글쓰기 때와 같은 다이얼로그를 열라고 알린다. 에러는 그대로 호출부로(문구는 code 기반).
  if (isHouseRuleRequiredError(error)) window.dispatchEvent(new CustomEvent(HOUSE_RULE_REQUIRED_EVENT));
  const requestConfig = error.config as (CustomAxiosRequestConfig & { _retry?: boolean }) | undefined;

  if (status === 401 && requestConfig && !requestConfig._retry) {
    // 🔴 TRAD-3291 — 재발급이 **원리적으로 가능한지** 먼저 가른다.
    //
    // 종전엔 401 이면 무조건 reissue 를 시도했다. 그래서 **로그인한 적도 없는 방문자**가
    // 인증 엔드포인트를 건드리면(비로그인 화면이 실수로 `apiWithAuth` 를 쓰는 경우 —
    // TRAD-3291 의 `/real-estate/watchlist` 가 실제 사례) 빈 쿠키로 reissue 를 쏘고,
    // 당연히 실패하고, 그 실패 처리로 **쿠키를 지우고 `auth:token-expired` 를 발화**했다.
    // 즉 세션이 없는 사람에게 "세션이 만료됐다"를 통보했다.
    //
    // 🔴 판별자를 `accessToken` 유무로 잡으면 **반대 방향으로 더 크게 깨진다.** accessToken
    // 쿠키는 maxAge 15분, refreshToken 은 30일이다(아래 setCookie 참조). 즉
    // **accessToken 부재 + refreshToken 생존 = 비로그인이 아니라 정상 로그인 상태**이고,
    // `AuthContext` 의 선제 갱신(TRAD-1698)이 다루는 바로 그 케이스다. 그 선제 갱신은
    // 마운트 시 1회만 도므로, 화면을 15분 넘게 열어둔 로그인 사용자에게는 **이 핸들러가
    // 유일한 복구 경로**다 — accessToken 으로 막으면 그 사용자 전원이 401 한 번에 로그아웃된다.
    //
    // 그래서 **reissue 요청이 실제로 싣는 값**(아래 `userId`·`refreshTokenKey`)으로 가른다.
    // 둘 중 하나라도 없으면 그 요청은 서버에 빈 값을 보내는 것뿐이라 성공할 수 없다.
    const userId = getCookie('userId');
    // TRAD-3861 — 리프레시 키는 서버 HttpOnly 쿠키라 여기서 읽을 수 없다(브라우저가 /api/auth 요청에 자동 동봉). 게이트는 userId 뿐.

    // (1) 로그인 흔적 자체가 없다 → 조용히 원래 에러로 끝낸다.
    //     `userId` 쿠키 유무는 `AuthContext` 의 비로그인 판정 기준과 같은 것이다
    //     (`checkAuth` 의 `const loginUserId = getCookie('userId'); if (loginUserId) …else
    //     setIsAuthenticated(false)`). 지울 쿠키도 없고 만료될 세션도 없으므로
    //     `auth:token-expired` 를 발화하지 않는다 — 발화하면 비로그인 방문자가 로그아웃
    //     처리를 받는다(= 이 티켓의 증상).
    if (!userId) {
      return Promise.reject(error);
    }

    // (2) `userId` 는 있는데 `refreshToken` 이 없는 갈래는 **의도적으로 넣지 않았다.**
    //
    //     이유: TRAD-3109 가드(`handlers-concurrent-refresh-failure.test.ts`)가 **정확히 그
    //     상태**를 fixture 로 pin 하고 있다(`userId='user-456'` + `refreshToken=undefined`).
    //     여기서 short-circuit 하면 리더가 `isRefreshing` 을 켜지 않아 팔로워 큐가 아예
    //     생기지 않고, 그 가드가 검사할 대상이 사라진다.
    //     그런데 그 fixture 는 자기 docstring 과 모순이다 — docstring 은 "비로그인"이라고
    //     하는데 비로그인에는 `userId` 쿠키가 없다. **어느 쪽이 진실인지는 TRAD-3109 원본
    //     사고를 읽기 전엔 알 수 없다.** 우리 추측으로 남의 회귀 가드를 고치면 그 가드가
    //     지키던 진짜 사고가 조용히 돌아온다.
    //
    //     대가는 작다: 성공할 수 없는 요청 1건이 더 나가는 것뿐이고, **이 티켓 전에도
    //     있던 동작**이다. 사용자에게 보이는 해가 없다.
    //
    //     🔴 재검토 조건: **TRAD-3295** 에서 그 fixture 가 정리되면(원본 티켓·커밋을 읽어
    //     실제 재현 조건을 확정한 뒤 fixture 와 docstring 을 일치시킨다) 이 갈래를 다시
    //     검토한다.

    if (isRefreshing) {
      // 이미 갱신 요청 중이면 대기 — reject도 반드시 큐에 같이 등록한다(TRAD-3109).
      // 성공 콜백만 등록하면 리더의 reissue가 실패했을 때 이 Promise가 영원히 pending으로
      // 남는다(위 helpers.ts 주석 참조).
      return new Promise((resolve, reject) => {
        addRefreshSubscriber(
          {
            resolve: (newToken: string) => {
              requestConfig.headers['Authorization'] = `Bearer ${newToken}`;
              resolve(apiWithAuth(requestConfig));
            },
            reject,
          },
          refreshSubscribers,
        );
      });
    }

    requestConfig._retry = true;
    isRefreshing = true;

    // TRAD-3291 — 게이트에서 읽은 값을 그대로 보낸다(게이트가 통과시킨 것과 다른 것을 보내지 않는다).
    // TRAD-3683 — 실패한 요청이 실었던 토큰을 넘겨, 탭 간 잠금 안에서 «다른 탭이 이미 갱신했나»를 가른다.
    const sentAuthorization = requestConfig.headers?.['Authorization'];
    const staleAccessToken =
      typeof sentAuthorization === 'string' && sentAuthorization.startsWith('Bearer ')
        ? sentAuthorization.slice('Bearer '.length)
        : undefined;
    const result = await reissueSession({ userId, staleAccessToken, secure: true });
    isRefreshing = false;

    if (result.kind === 'TOKEN') {
      onRefreshed(result.accessToken, refreshSubscribers);
      // 원래 요청 재시도 — `_retry` 가 켜져 있어 이 요청은 다시 갱신하지 않는다(1회 한정).
      requestConfig.headers['Authorization'] = `Bearer ${result.accessToken}`;
      if (result.source === 'REISSUED') return apiWithAuth(requestConfig);
      // TRAD-3683 — 다른 탭이 갱신한 토큰으로 재시도했는데도 401 이면 그때는 세션 끝으로 본다(팀장 10/11: 재시도 1회 ·
      // 그 결과로 판정). 404 는 원 요청 자원의 «없음»이라 세션과 무관 — 401 만 본다.
      return apiWithAuth(requestConfig).catch((retryError: AxiosError) => {
        if (retryError?.response?.status === 401) expireSession();
        return Promise.reject(retryError);
      });
    }

    // TRAD-3683 — ~~실패 이유 무관하게 쿠키 제거~~ → 401·404(세션 끝)만 제거. 네트워크·5xx·403 은 토큰을 남기고 원 요청만 실패.
    if (result.outcome === 'LOGOUT') expireSession();
    // TRAD-3109 — 대기 중이던 팔로워 요청들도 같은 에러로 reject한다(LOGOUT·KEEP 둘 다). 안 하면 그 요청들의
    // Promise가 영원히 pending으로 남아(react-query라면 isLoading이 영원히 true) 화면이
    // 스켈레톤에 갇힌다(비로그인 `/user/:userId`에서 실측).
    onRefreshFailed(result.error, refreshSubscribers);
    return Promise.reject(result.error);
  }

  return Promise.reject(error);
};
